import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import api from '../../services/api';

const categories = ['Technology', 'Business', 'Design', 'Marketing', 'Career'];

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', excerpt: '', category: 'Technology', tags: '', status: 'draft', featured: false });
  const [message, setMessage] = useState('');

  useEffect(() => { fetchBlogs(); }, []);

  const fetchBlogs = async () => {
    try { const res = await api.get('/blogs?limit=100'); setBlogs(res.data.blogs); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/blogs/${editing._id}`, data);
      else await api.post('/blogs', data);
      setMessage(editing ? 'Blog updated successfully.' : 'Blog created successfully.');
      setShowModal(false);
      setEditing(null);
      setFormData({ title: '', content: '', excerpt: '', category: 'Technology', tags: '', status: 'draft', featured: false });
      fetchBlogs();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Unable to save blog.');
    }
  };

  const handleDelete = async (id) => { if (window.confirm('Delete this blog?')) { await api.delete(`/blogs/${id}`); fetchBlogs(); } };

  if (loading) return <div className="animate-pulse h-96 bg-gray-200 rounded-xl"></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Blog Posts</h2>
        {message && <span className="text-sm font-medium text-cyan-600">{message}</span>}
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700"><Plus size={20} /> Add Blog</button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th></tr></thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {blogs.map((b) => (
              <tr key={b._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 font-medium text-gray-800 dark:text-white">{b.title}</td>
                <td className="px-6 py-4"><span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 rounded">{b.category}</span></td>
                <td className="px-6 py-4"><span className={`px-2 py-1 text-xs rounded-full ${b.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{b.status}</span></td>
                <td className="px-6 py-4 text-gray-500">{b.views}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => { setEditing(b); setFormData({ ...b, tags: b.tags?.join(', ') }); setShowModal(true); }} className="p-2 text-gray-500 hover:text-cyan-600"><Edit2 size={16} /></button>
                  <button onClick={() => handleDelete(b._id)} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {blogs.length === 0 && <div className="p-8 text-center text-gray-500">No blog posts</div>}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b"><h3 className="text-xl font-semibold">{editing ? 'Edit Blog' : 'Add Blog'}</h3><button onClick={() => setShowModal(false)}><X size={24} /></button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <input type="text" placeholder="Blog Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" required />
              <textarea placeholder="Short Excerpt" value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={2} />
              <textarea placeholder="Blog Content (HTML supported)" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={10} required />
              <div className="grid grid-cols-2 gap-4">
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600">{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"><option value="draft">Draft</option><option value="published">Published</option></select>
              </div>
              <input type="text" placeholder="Tags (comma-separated)" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 rounded" /> Featured</label>
              <div className="flex gap-4 pt-4"><button type="submit" className="flex-1 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">{editing ? 'Update' : 'Create'}</button><button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
